import React, { useCallback, useState } from 'react';
import { Zap, Plus, X } from 'lucide-react';
import SectionShell from './SectionShell';

const KIND_OPTIONS = [
  { key: 'electrical', label: 'Electrical' },
  { key: 'mechanical', label: 'Mechanical' },
  { key: 'hydraulic',  label: 'Hydraulic' },
  { key: 'pneumatic',  label: 'Pneumatic' },
  { key: 'gravity',    label: 'Gravity / suspended' },
  { key: 'thermal',    label: 'Thermal' },
  { key: 'chemical',   label: 'Chemical' },
  { key: 'stored',     label: 'Stored energy (spring/capacitor)' },
];

const ISOLATION_METHODS = [
  'Open, tag, and lock',
  'Grounded and tested dead',
  'Blocked / pinned',
  'Bled and vented',
  'Cooled and verified',
  'Chained / physically barriered',
];

/**
 * EnergySourcesSection — list of {kind, source, isolation_method, verified_by}
 * Each row explicitly captures WHO verified the isolation — required for
 * audit trail.
 */
export default function EnergySourcesSection({ draft, onChange }) {
  const sources = draft.energy_sources || [];
  const [kind, setKind] = useState('');
  const [source, setSource] = useState('');
  const [method, setMethod] = useState('');
  const [verifiedBy, setVerifiedBy] = useState('');

  const removeAt = useCallback((idx) => {
    const next = sources.slice();
    next.splice(idx, 1);
    onChange({ energy_sources: next });
  }, [sources, onChange]);

  const add = useCallback(() => {
    if (!kind || !source.trim()) return;
    onChange({
      energy_sources: [
        ...sources,
        { kind, source: source.trim(), isolation_method: method, verified_by: verifiedBy.trim() },
      ],
    });
    setKind(''); setSource(''); setMethod(''); setVerifiedBy('');
  }, [kind, source, method, verifiedBy, sources, onChange]);

  return (
    <SectionShell
      icon={Zap}
      title="Energy Source Analysis"
      subtitle="List every energy source, how it is isolated, and who verified the isolation."
      color="#fbbf24"
      badge={sources.length ? `${sources.length} source${sources.length === 1 ? '' : 's'}` : 'None'}
      testId="jbv2-section-energy"
    >
      {sources.length === 0 && (
        <div className="jbv2-empty" data-testid="jbv2-energy-empty">
          No energy sources listed. If the job truly has zero energy sources (extremely rare),
          add a note under Scope. Otherwise, add every source below.
        </div>
      )}
      {sources.map((s, i) => (
        <div key={i} className="jbv2-row" data-testid={`jbv2-energy-row-${i}`}>
          <div className="jbv2-row-body">
            <div className="jbv2-row-cat" style={{ textTransform: 'capitalize' }}>{s.kind}</div>
            <div className="jbv2-row-note">
              <strong>Source:</strong> {s.source || '—'} · <strong>Isolation:</strong> {s.isolation_method || '—'}
              {s.verified_by && <> · <strong>Verified by:</strong> {s.verified_by}</>}
            </div>
          </div>
          <div className="jbv2-row-actions">
            <button
              type="button"
              className="jbv2-row-btn jbv2-row-btn--danger"
              onClick={() => removeAt(i)}
              data-testid={`jbv2-energy-remove-${i}`}
              aria-label="Remove energy source"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      ))}

      <div className="jbv2-add-row" data-testid="jbv2-energy-add-row">
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          data-testid="jbv2-energy-kind"
          aria-label="Energy kind"
        >
          <option value="">Kind…</option>
          {KIND_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <input
          type="text"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          placeholder="Source (e.g. Feeder 12L, Compressor #3)"
          data-testid="jbv2-energy-source"
          aria-label="Energy source"
        />
        <select
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          data-testid="jbv2-energy-method"
          aria-label="Isolation method"
        >
          <option value="">Isolation method…</option>
          {ISOLATION_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <input
          type="text"
          value={verifiedBy}
          onChange={(e) => setVerifiedBy(e.target.value)}
          placeholder="Verified by (name)"
          data-testid="jbv2-energy-verified-by"
          aria-label="Verified by"
        />
        <button
          type="button"
          onClick={add}
          disabled={!kind || !source.trim()}
          data-testid="jbv2-energy-add-btn"
        >
          <Plus size={13} /> Add source
        </button>
      </div>
    </SectionShell>
  );
}
